import React from 'react';
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
} from 'reactstrap';
import { Link, navigate } from 'gatsby';
import TextInput from '../inputs/TextInput';
import PhoneInput from '../inputs/PhoneInput';
import OnOffSwitch from '../OnOffSwitch';
import { StaffContextType, UserContext } from '../../../context/UserContext';
import {
  ProfileContext,
  StaffProfileContextType,
} from '../../../context/ProfileContext';
import { SmsPreferencesData } from '../../account-settings/SmsPreferences';
import { isNewUserFlow } from '../../../utils/userFlowStorage';
import agent from '../../../agent';
import {
  toAccountSettings,
  toDashboard,
  toProfile,
  toResume,
} from '../../../routes';
import { sanitizeCompanyDates } from '../../../utils/collectionHelper';
import {
  FaCheck,
  FaCheckCircle,
  FaFile,
  FaFileUpload,
  FaInfoCircle,
  FaMobileAlt,
  FaBell,
  FaUserCircle,
} from 'react-icons/fa';
import { AxiosError } from 'axios';

// Progressive nudge — show optional-field prompt on first login, then every 30 days.
const NUDGE_KEY = (userId: string | number) =>
  `dp_legacy_profile_nudge_${userId}`;

const RESUME_UPLOAD_FOLLOWUP_KEY = 'dp_resume_upload_followup';
const NUDGE_INTERVAL_FIRST_MS = 7 * 24 * 60 * 60 * 1000;
const NUDGE_INTERVAL_MS = 30 * 24 * 60 * 60 * 1000;

interface NudgeRecord {
  viewedAt: string;
  viewCount: number;
}

function readNudgeRecord(userId: string | number): NudgeRecord | null {
  try {
    const raw = localStorage.getItem(NUDGE_KEY(userId));
    return raw ? (JSON.parse(raw) as NudgeRecord) : null;
  } catch {
    return null;
  }
}

function writeNudgeRecord(userId: string | number, viewCount: number): void {
  try {
    const record: NudgeRecord = {
      viewedAt: new Date().toISOString(),
      viewCount,
    };
    localStorage.setItem(NUDGE_KEY(userId), JSON.stringify(record));
  } catch {}
}

function shouldShowNudge(userId: string | number): boolean {
  const record = readNudgeRecord(userId);
  if (!record) return true;
  const interval =
    record.viewCount === 1 ? NUDGE_INTERVAL_FIRST_MS : NUDGE_INTERVAL_MS;
  return Date.now() - new Date(record.viewedAt).getTime() >= interval;
}

const STEPS = [
  { skippable: false }, // 0: Name
  { skippable: true }, // 1: Resume upload
  { skippable: true }, // 2: Visibility
  { skippable: true }, // 3: Phone
  { skippable: true }, // 4: SMS preferences
];

const LegacyProfileSetupModal = () => {
  const { user, updateUser } = React.useContext(
    UserContext
  ) as StaffContextType;
  const { profile, updateProfile, setParsedResume } = React.useContext(
    ProfileContext
  ) as StaffProfileContextType;
  const [isOpen, setIsOpen] = React.useState(false);
  const isOpenRef = React.useRef(isOpen);
  isOpenRef.current = isOpen;
  const [step, setStep] = React.useState(0);
  const [isComplete, setIsComplete] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  // Step 0: Name
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');

  // Step 1: Resume upload
  const [resumeFile, setResumeFile] = React.useState<File | null>(null);
  const resumeFileInputRef = React.useRef<HTMLInputElement>(null);

  // Step 2: Visibility
  const [isPublic, setIsPublic] = React.useState(false);
  const [isRelocate, setIsRelocate] = React.useState(false);

  // Step 3: Phone
  const [phone, setPhone] = React.useState('');

  // Step 4: SMS preferences
  const [smsMessages, setSmsMessages] = React.useState(false);
  const [smsInvites, setSmsInvites] = React.useState(false);
  const [smsTemp, setSmsTemp] = React.useState(false);

  React.useEffect(() => {
    if (!user || !profile || isOpenRef.current) return;

    const missingName = !user.firstName || !user.lastName;

    // Missing name: always show at step 0, bypass nudge
    if (missingName) {
      setInitialState();
      setStep(0);
      setIsOpen(true);
      return;
    }

    // Resume-upload follow-up: re-open at the first incomplete step after the
    // user saves their profile on the resume page.
    if (sessionStorage.getItem(RESUME_UPLOAD_FOLLOWUP_KEY) === '1') {
      sessionStorage.removeItem(RESUME_UPLOAD_FOLLOWUP_KEY);
      const nextStep = !profile.isPublic
        ? 2
        : !user.phone
          ? 3
          : !user.preferences?.smsMessages &&
              !user.preferences?.smsInvites &&
              !user.preferences?.smsTemp
            ? 4
            : null;
      if (nextStep !== null) {
        setInitialState();
        setStep(nextStep);
        setIsOpen(true);
      }
      return;
    }

    // New user flow (post-registration or first application): bypass resume upload step and
    // show visibility step with isPublic defaulting to true. Legacy users' preferences
    // are respected — only onboarding/onboarding_application flows auto-set public.
    if (isNewUserFlow()) {
      setInitialState();
      defaultNewUserToPublic();
      return;
    }

    // Optional fields: only nudge once every 30 days
    const hasIncompleteOptionalData =
      !profile.intro ||
      !profile.isPublic ||
      !user.phone ||
      (!user.preferences?.smsMessages &&
        !user.preferences?.smsInvites &&
        !user.preferences?.smsTemp);

    if (hasIncompleteOptionalData && shouldShowNudge(user.id)) {
      const record = readNudgeRecord(user.id);
      writeNudgeRecord(user.id, (record?.viewCount ?? 0) + 1);
      setInitialState();
      if (!profile.intro) {
        setStep(1);
      } else if (!profile.isPublic) {
        setStep(2);
      } else if (!user.phone) {
        setStep(3);
      } else {
        setStep(4);
      }
      setIsOpen(true);
    }
  }, [user, profile]);

  async function defaultNewUserToPublic() {
    try {
      const response = await agent.Staff.updateVisibility({ state: true });
      if (response.status === 200) {
        updateProfile({ isPublic: response.data.isPublic });
      }
    } catch (err) {
      const error = err as AxiosError;
      setError(error.message);
    } finally {
      setIsPublic(true); // pre-set the toggle regardless of API result
      setStep(2);
      setIsOpen(true);
    }
  }

  function setInitialState() {
    setFirstName(user.firstName ?? '');
    setLastName(user.lastName ?? '');
    setIsPublic(profile.isPublic ?? false);
    setIsRelocate(profile.isRelocate ?? false);
    setPhone(user.phone ?? '');
    setSmsMessages(user.preferences?.smsMessages ?? false);
    setSmsInvites(user.preferences?.smsInvites ?? false);
    setSmsTemp(user.preferences?.smsTemp ?? false);
  }

  const canContinue =
    step !== 0 || (firstName.trim().length > 0 && lastName.trim().length > 0);

  async function handleContinue() {
    setError('');
    setIsSubmitting(true);
    try {
      if (step === 0) {
        const body = {
          ...user,
          ...profile,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          skills: profile.skills.map((skill) => skill.skill),
          companies: profile.companies.map(sanitizeCompanyDates),
        };
        await agent.Staff.saveResume({ body });
        updateUser({ firstName: body.firstName, lastName: body.lastName });
        advance();
      } else if (step === 1) {
        if (resumeFile) {
          const response = await agent.Staff.uploadResume(resumeFile);
          setParsedResume(response.data.resume);
          editProfile();
          return;
        } else {
          editProfile();
          return;
        }
      } else if (step === 2) {
        const response = await Promise.all([
          agent.Staff.updateVisibility({ state: isPublic }),
          agent.Staff.updateRelocate({ state: isRelocate }),
        ]);
        updateProfile({
          isPublic: response[0].data.isPublic,
          isRelocate: response[1].data.isRelocate,
        });
        advance();
      } else if (step === 3) {
        const response = await agent.Auth.updatePhone({ phone });
        updateUser({ phone: response.data.phone });
        advance();
      } else if (step === 4) {
        const prefs: Partial<SmsPreferencesData> = {
          smsMessages,
          smsInvites,
          smsTemp,
        };
        const response = await agent.Staff.updateAlertPreferences(prefs);
        updateUser({
          preferences: {
            ...user.preferences,
            smsMessages: response.data.smsMessages,
            smsInvites: response.data.smsInvites,
            smsTemp: response.data.smsTemp,
          },
        });
        advance();
      }
    } catch (err) {
      const error = err as AxiosError;
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  function editProfile() {
    sessionStorage.setItem(RESUME_UPLOAD_FOLLOWUP_KEY, '1');
    navigate(toResume);
    setIsOpen(false);
  }

  function handleSkip() {
    setError('');
    advance();
  }

  function advance() {
    if (step >= STEPS.length - 1) {
      setIsComplete(true);
    } else {
      setStep((s) => s + 1);
    }
  }

  const progress = isComplete ? 100 : ((step + 1) / STEPS.length) * 100;

  return (
    <Modal
      isOpen={isOpen}
      centered
      toggle={() => {
        if (step === 5) {
          setIsOpen(!isOpen);
        }
      }}
    >
      <div className="d-flex justify-content-between align-items-center w-100">
        <ModalHeader data-testid="modal-header">
          A Few Profile Updates
        </ModalHeader>
        <small data-testid="step-counter" className="text-muted me-3">
          {isComplete ? 'Complete' : `Step ${step + 1} of ${STEPS.length}`}
        </small>
      </div>
      <Progress
        value={progress}
        aria-label="Form completion progress"
        style={{
          height: 3,
          borderRadius: 0,
          backgroundColor: 'var(--dp-border-default)',
        }}
        barStyle={{
          backgroundColor: 'var(--dp-nav-active-border)',
          transition: 'width 0.35s ease',
        }}
      />
      {isComplete ? (
        <ModalBody className="text-center py-5">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
            style={{
              width: 52,
              height: 52,
              backgroundColor: 'var(--dp-focus-tint)',
            }}
          >
            <FaCheck
              style={{ color: 'var(--dp-primary-blue)', fontSize: 20 }}
            />
          </div>
          <h6 data-testid="step-complete-heading" className="fw-semibold mb-2">
            You're all set!
          </h6>
          <p className="text-muted mb-4">
            Your profile is up to date. Head to your dashboard to explore jobs
            and manage your applications.
          </p>
          <Button
            tag={Link}
            to={toDashboard}
            color="primary"
            className="w-100"
            onClick={() => setIsOpen(false)}
          >
            Go to my dashboard
          </Button>
        </ModalBody>
      ) : (
        <>
          <ModalBody>
            {step === 0 && (
              <>
                <StepIcon
                  bg="var(--dp-chip-orange-bg)"
                  icon={
                    <FaUserCircle
                      style={{ color: 'var(--dp-nav-active-text)' }}
                    />
                  }
                />
                <h6 data-testid="step-name-heading" className="h5 mb-1">
                  Let's update your name
                </h6>
                <p className="text-muted mb-3">
                  We've updated how names are stored. Please confirm your first
                  and last name so your profile looks right to employers.
                </p>
                <div className="row g-2">
                  <div className="col">
                    <TextInput
                      name="firstName"
                      label="First Name"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="col">
                    <TextInput
                      name="lastName"
                      label="Last Name"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <StepIcon
                  bg="var(--dp-light-blue)"
                  icon={
                    <FaFileUpload style={{ color: 'var(--dp-primary-blue)' }} />
                  }
                />
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h6
                    data-testid="step-resume-heading"
                    className="fw-semibold mb-0"
                  >
                    Import your resume
                  </h6>
                  <Badge
                    color="primary"
                    style={{ fontSize: '0.7rem', padding: '0.15em 0.55em' }}
                  >
                    New
                  </Badge>
                </div>
                <p className="text-muted mb-3">
                  Upload a PDF or Word file and our AI will automatically
                  extract your work history, education, and skills — saving you
                  time filling in your profile.
                </p>
                {resumeFile ? (
                  <div
                    className="d-flex align-items-center gap-2 p-3 rounded mb-3"
                    style={{
                      backgroundColor: 'var(--dp-light-blue)',
                      border: '1px solid var(--dp-chip-teal-text)',
                    }}
                  >
                    <FaFile
                      className="flex-shrink-0"
                      style={{ color: 'var(--dp-chip-teal-text)' }}
                    />
                    <small
                      data-testid="selected-file-name"
                      className="flex-grow-1 text-truncate fw-semibold"
                      style={{ color: 'var(--dp-chip-teal-text)' }}
                    >
                      {resumeFile.name}
                    </small>
                    <button
                      type="button"
                      className="btn-close btn-sm flex-shrink-0"
                      aria-label="Remove file"
                      onClick={() => setResumeFile(null)}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className="w-100 rounded p-4 mb-3 text-center border-0"
                    style={{
                      border: '2px dashed var(--dp-border-default)',
                      cursor: 'pointer',
                    }}
                    onClick={() => resumeFileInputRef.current?.click()}
                  >
                    <FaFileUpload
                      className="d-block mx-auto mb-2"
                      style={{
                        fontSize: 24,
                        color: 'var(--dp-text-placeholder)',
                      }}
                    />
                    <small
                      data-testid="file-upload-hint"
                      className="text-muted"
                    >
                      Click to choose a file &mdash; PDF or Word (.docx), max 10
                      MB
                    </small>
                  </button>
                )}
                <input
                  ref={resumeFileInputRef}
                  type="file"
                  accept=".pdf,.docx"
                  className="d-none"
                  onChange={(e) => setResumeFile(e.target.files?.[0] ?? null)}
                />
                {!profile.intro && (
                  <InfoBox variant="orange">
                    <strong>Your profile is incomplete.</strong> Profiles
                    without a summary are unable to apply to jobs or receive
                    interview invitations from employers. Completing this step
                    will unlock those features.
                  </InfoBox>
                )}
                <InfoBox variant="teal">
                  <strong>Powered by AI.</strong> Our AI reads your resume and
                  prefills your profile — you review and confirm everything
                  before it's saved.
                </InfoBox>
              </>
            )}

            {step === 2 && (
              <>
                <StepIcon
                  bg="var(--dp-focus-tint)"
                  icon={
                    <FaCheckCircle
                      style={{ color: 'var(--dp-primary-blue)' }}
                    />
                  }
                />
                <h6
                  data-testid="step-visibility-heading"
                  className="fw-semibold mb-1"
                >
                  Make your profile visible
                </h6>
                <p className="text-muted mb-3">
                  A public profile lets employers find and invite you to apply —
                  even for jobs you haven't searched yet.
                </p>
                <InfoBox variant="teal">
                  <strong>Profiles marked public get hired 2× faster.</strong>
                </InfoBox>
                <div>
                  <ToggleRow
                    label="Make profile public"
                    description="Employers can discover you in resume search"
                  >
                    <OnOffSwitch
                      name="isPublic"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                    />
                  </ToggleRow>
                  <ToggleRow
                    label="Open to relocation"
                    description="Employers may offer relocation assistance"
                    noBorder
                  >
                    <OnOffSwitch
                      name="isRelocate"
                      checked={isRelocate}
                      onChange={(e) => setIsRelocate(e.target.checked)}
                    />
                  </ToggleRow>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <StepIcon
                  bg="var(--dp-chip-orange-bg)"
                  icon={
                    <FaMobileAlt
                      style={{ color: 'var(--dp-nav-active-text)' }}
                    />
                  }
                />
                <h6
                  data-testid="step-phone-heading"
                  className="fw-semibold mb-1"
                >
                  New: SMS job alerts
                </h6>
                <p className="text-muted mb-3">
                  Get real-time text notifications for messages, interview
                  invites, and temp job openings. Enter your mobile number to
                  get started.
                </p>
                <PhoneInput
                  name="phone"
                  label="Mobile number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
                <InfoBox variant="teal">
                  <strong>Privacy &amp; Trust:</strong> Your number is used only
                  for notifications and is never shared in resume search
                  results. Standard message rates may apply.
                </InfoBox>
              </>
            )}

            {step === 4 && (
              <>
                <StepIcon
                  bg="var(--dp-chip-orange-bg)"
                  icon={
                    <FaBell style={{ color: 'var(--dp-nav-active-text)' }} />
                  }
                />
                <h6 data-testid="step-sms-heading" className="fw-semibold mb-1">
                  Choose your SMS alerts
                </h6>
                <p className="text-muted mb-3">
                  Select which notifications you'd like to receive. You can
                  update these anytime in Settings.
                </p>
                <InfoBox variant="orange">
                  Sending to <strong>{phone || 'your number'}</strong>. You can
                  change this in{' '}
                  <Link to={toAccountSettings} color="primary">
                    Settings
                  </Link>
                  .
                </InfoBox>
                <div>
                  <ToggleRow
                    label="SMS for messages"
                    description="When an employer sends you a message"
                  >
                    <OnOffSwitch
                      name="smsMessages"
                      checked={smsMessages}
                      onChange={(e) => setSmsMessages(e.target.checked)}
                    />
                  </ToggleRow>
                  <ToggleRow
                    label="SMS for invites"
                    description="When an employer invites you to apply"
                  >
                    <OnOffSwitch
                      name="smsInvites"
                      checked={smsInvites}
                      onChange={(e) => setSmsInvites(e.target.checked)}
                    />
                  </ToggleRow>
                  <ToggleRow
                    label="SMS for temp jobs"
                    description="Alerts for temporary job opportunities"
                    noBorder
                  >
                    <OnOffSwitch
                      name="smsTemp"
                      checked={smsTemp}
                      onChange={(e) => setSmsTemp(e.target.checked)}
                    />
                  </ToggleRow>
                </div>
              </>
            )}

            {error && (
              <p
                data-testid="form-error"
                className="text-danger small mt-2 mb-0"
              >
                {error}
              </p>
            )}
          </ModalBody>

          <ModalFooter className="d-flex justify-content-between align-items-center">
            {/* Step dots */}
            <div className="d-flex gap-1 align-items-center" aria-hidden="true">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor:
                      i < step
                        ? 'var(--dp-focus-ring-color)'
                        : i === step
                          ? 'var(--dp-nav-active-border)'
                          : 'var(--dp-border-default)',
                  }}
                  onClick={() => {
                    if (canContinue) setStep(i);
                  }}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="d-flex gap-2">
              {STEPS[step].skippable && (
                <Button
                  color="secondary"
                  outline
                  onClick={handleSkip}
                  disabled={isSubmitting}
                >
                  Skip for now
                </Button>
              )}
              <Button
                color="primary"
                onClick={handleContinue}
                disabled={!canContinue || isSubmitting}
              >
                {isSubmitting
                  ? 'Saving…'
                  : step === 1 && resumeFile
                    ? 'Upload & go to profile'
                    : step === STEPS.length - 1
                      ? 'Save & finish'
                      : 'Continue'}
              </Button>
            </div>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

// Small helpers to keep step JSX tidy

const StepIcon = ({ bg, icon }: { bg: string; icon: React.ReactNode }) => (
  <div
    className="rounded-circle d-flex align-items-center justify-content-center mb-3"
    style={{ width: 40, height: 40, backgroundColor: bg }}
  >
    {icon}
  </div>
);

const InfoBox = ({
  variant,
  children,
}: {
  variant: 'teal' | 'orange';
  children: React.ReactNode;
}) => (
  <div
    className="d-flex gap-2 p-3 rounded mb-3"
    style={{
      backgroundColor:
        variant === 'teal'
          ? 'var(--dp-light-blue)'
          : 'var(--dp-chip-orange-bg)',
      border: `1px solid ${variant === 'teal' ? 'var(--dp-chip-teal-text)' : 'var(--dp-chip-orange-text)'}`,
      color:
        variant === 'teal'
          ? 'var(--dp-chip-teal-text)'
          : 'var(--dp-chip-orange-text)',
    }}
  >
    <FaInfoCircle className="flex-shrink-0 mt-1" />
    <small>{children}</small>
  </div>
);

const ToggleRow = ({
  label,
  description,
  noBorder,
  children,
}: {
  label: string;
  description: string;
  noBorder?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={`d-flex justify-content-between align-items-center py-3 ${noBorder ? '' : 'border-bottom'}`}
  >
    <div>
      <p className="mb-0 fw-semibold">{label}</p>
      <small className="text-muted">{description}</small>
    </div>
    {children}
  </div>
);

export default LegacyProfileSetupModal;
